import * as Yup from 'yup'
import { isBoolean, isEmpty, isNil, size, some, uniq } from 'lodash'

import { carriedWeight } from '@/views/Organizations/components/connectionWeight.helpers'
import { ORG_NEW_FILES_PAUSED_LABEL } from '@/views/Organizations/components/OrgSyncPausedBanner'

// Mirror the agent-gpt server-side validators for fast feedback; the server
// stays the source of truth.
export const GUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
// Accept a pasted site URL (scheme + trailing slash); the server strips the
// scheme on write, so submitting the pasted form is fine.
export const HOST_RE = /^(https?:\/\/)?[a-z0-9-]+\.sharepoint\.(com|us)\/?$/i
// QCER-1089 product decision: employees' personal OneDrive sites are never
// ingested. AI Answers' corpus is org-wide, so one person's private content
// would become readable by their whole company. The server refuses these too
// (`sharepoint_validation.py`); these two mirrors exist so the operator sees
// WHY inline instead of hitting a 422 on Save.
//
// Personal sites live on the `<tenant>-my.sharepoint.com` host AND under a
// `/personal/<upn>` path, and either alone leaves a way in — a connection can
// name the ordinary org host with a personal path.
export const PERSONAL_HOST_RE = /^(https?:\/\/)?[a-z0-9-]*-my\.sharepoint\.(com|us)\/?$/i
// Anchored at the path root and case-insensitive (SharePoint paths are), so
// `/Personal/x` is caught but a team site named `/sites/personal-development`
// is not.
const PERSONAL_SITE_PATH_RE = /^\/personal(\/|$)/i
export const PERSONAL_SITE_MESSAGE =
  'Personal OneDrive sites cannot be ingested — AI Answers is org-wide, so the content would be readable by everyone in the organization. Use a team site, e.g. /sites/Knowledge'

// Selector identity, mirrored from the agent-gpt resolver: a blank or '*'
// library means the whole site, and specific library names compare
// case-insensitively. A list name has no whole-site spelling.
export const normalizedLibrary = (libraryName) => (libraryName || '').trim()

export const isSiteWideSelector = (libraryName) =>
  ['', '*'].includes(normalizedLibrary(libraryName))

export const sitePathOf = (record) => (record.site_path || '').trim()

// Sites compare trimmed and case-insensitively, as Graph, the server's list coverage and
// its notebook prune do; removal targeting of pages and libraries still compares exactly.
export const sameSite = (recordA, recordB) =>
  sitePathOf(recordA).toLowerCase() === sitePathOf(recordB).toLowerCase()

const sameName = (nameA, nameB) =>
  normalizedLibrary(nameA).toLowerCase() === normalizedLibrary(nameB).toLowerCase()

const sameLibraryName = (recordA, recordB) => sameName(recordA.library_name, recordB.library_name)

// A row's Type (QCER-1089). Kept as a UI-only field: it decides which server
// key the row is submitted under, and the server's two name keys are mutually
// exclusive, so a single "name" input plus a type is both simpler to fill in and
// impossible to submit in a shape the server rejects. Type expresses all three
// selector shapes; lists are a connection-level setting.
export const WHOLE_SITE_TYPE = 'site'
export const LIBRARY_TYPE = 'library'
export const LIST_TYPE = 'list'
export const SELECTOR_TYPE_OPTIONS = [
  { value: WHOLE_SITE_TYPE, label: 'Whole site' },
  { value: LIBRARY_TYPE, label: 'One library' },
  { value: LIST_TYPE, label: 'One list' },
]
const SELECTOR_TYPES = SELECTOR_TYPE_OPTIONS.map((option) => option.value)

// Whole-site rows index everything on the site, so they take no name.
export const namesOneThing = (row) =>
  row.selector_type === LIBRARY_TYPE || row.selector_type === LIST_TYPE

// Every row must name a site, and a row whose Type names one thing must name
// it — there is no blank-means-whole-site spelling in the form any more. At
// least one row is required; an empty allowlist is not allowed.
export const ALLOWLIST_ROW_SCHEMA = Yup.object({
  site_path: Yup.string()
    .required('Site is required')
    .matches(/^\//, "Site path must start with '/', e.g. /sites/Marketing")
    .test(
      'not-a-personal-site',
      PERSONAL_SITE_MESSAGE,
      (value) => !PERSONAL_SITE_PATH_RE.test((value || '').trim())
    ),
  selector_type: Yup.string().oneOf(SELECTOR_TYPES),
  // yup 0.32: `then`/`otherwise` are SCHEMAS, not schema-returning
  // functions (that is the 1.x signature, and passing a function here
  // silently fails the whole row instead of applying the branch).
  name: Yup.string().when('selector_type', {
    is: (value) => value === LIBRARY_TYPE || value === LIST_TYPE,
    then: Yup.string().trim().required('Name is required for this type'),
    otherwise: Yup.string(),
  }),
})

export const ALLOWLIST_SCHEMA = Yup.array().of(ALLOWLIST_ROW_SCHEMA).min(1, 'Add at least one site')

export const BLANK_ALLOWLIST_ROW = {
  site_path: '',
  selector_type: WHOLE_SITE_TYPE,
  name: '',
  // Empty = answers every playbook (QCER-1075), the same default an upload
  // with no playbooks selected already has.
  config_cids: [],
}

// A row keeps its loaded include_lists, so a Save writes each row's own value back.
const formIncludeLists = (entry) =>
  isBoolean(entry.include_lists) ? entry.include_lists : undefined

// Server entry -> form row, one Type per shape.
const toFormRow = (entry) => {
  // config_cids is always an array so the multi-select never sees undefined;
  // the server omits the key entirely on an unscoped entry.
  const base = {
    site_path: entry.site_path || '',
    config_cids: entry.config_cids || [],
    // No column edits it; carried so Save writes the stored weight back.
    ...carriedWeight(entry),
    include_lists: formIncludeLists(entry),
  }
  if (!isEmpty(normalizedLibrary(entry.list_name))) {
    return { ...base, selector_type: LIST_TYPE, name: entry.list_name }
  }
  if (!isSiteWideSelector(entry.library_name)) {
    return { ...base, selector_type: LIBRARY_TYPE, name: entry.library_name }
  }
  return { ...base, selector_type: WHOLE_SITE_TYPE, name: '' }
}

export const toFormAllowlist = (allowlist) =>
  isEmpty(allowlist) ? [{ ...BLANK_ALLOWLIST_ROW }] : allowlist.map(toFormRow)

// Form row -> server entry. Only the keys that apply are sent: the server
// refuses an entry carrying both name keys, and refuses `include_lists` on a
// list row. Because Type is the single source of the row's shape, a row cannot
// carry a value its shape does not allow.
export const toServerEntry = (row, { newRowLists } = {}) => {
  const site_path = sitePathOf(row)
  const name = normalizedLibrary(row.name)
  // Omitted rather than sent as [] when nothing is selected: the server stores
  // "answers every playbook" as an absent key, which is exactly what every row
  // written before QCER-1075 already holds — so an untouched allowlist
  // round-trips byte-identical and needs no migration.
  const scope = isEmpty(row.config_cids) ? {} : { config_cids: row.config_cids }
  const carried = { ...scope, ...carriedWeight(row) }
  if (row.selector_type === LIST_TYPE) {
    return { site_path, list_name: name, ...carried }
  }
  // A row writes back what it loaded; a new row takes the connection's shared setting.
  let lists = {}
  if (isBoolean(row.include_lists)) {
    lists = { include_lists: row.include_lists }
  } else if (isBoolean(newRowLists)) {
    lists = { include_lists: newRowLists }
  }
  const library_name = row.selector_type === LIBRARY_TYPE ? name : ''
  return { site_path, library_name, ...lists, ...carried }
}

export const toServerAllowlist = (rows, options) => rows.map((row) => toServerEntry(row, options))

// Does this SERVER entry select a list? Mirrors selection.selects_a_list: a list
// entry carries no library_name, whose blank form is the whole-site spelling, so
// the list key has to be checked FIRST or a list entry reads as whole-site.
export const entrySelectsAList = (entry) => !isEmpty(normalizedLibrary(entry.list_name))

// The include_lists every site and library row shares, which a new row takes, so a
// keyless connection's new rows stay keyless and follow the default.
export const sharedIncludeLists = (allowlist) => {
  const values = uniq(
    allowlist.filter((entry) => !entrySelectsAList(entry)).map((entry) => entry.include_lists)
  )
  return size(values) === 1 && isBoolean(values[0]) ? values[0] : undefined
}

// Mirrors the API: a missing include_lists takes the connection's lists_default.
export const entryDiscoversLists = (entry, listsDefault = false) =>
  !entrySelectsAList(entry) && (isBoolean(entry.include_lists) ? entry.include_lists : listsDefault)

// Whether the site of `record` (an entry or a sync row) discovers its lists.
// Mirrors site_discovers_lists: no explicit false on the site, and one entry opts in.
export const siteDiscoversLists = (entries, record, listsDefault = false) => {
  const siteEntries = entries.filter(
    (entry) => !entrySelectsAList(entry) && sameSite(entry, record)
  )
  return (
    siteEntries.every((entry) => entry.include_lists !== false) &&
    siteEntries.some((entry) => entryDiscoversLists(entry, listsDefault))
  )
}

export const connectionHasSiteRows = (allowlist) =>
  allowlist.some((entry) => !entrySelectsAList(entry))

// On while any site discovers its lists, so a per-site opt-out set through the
// API leaves the connection on. Named lists alone always sync, so that reads as on.
export const connectionListsEnabled = (allowlist, listsDefault = false) =>
  !connectionHasSiteRows(allowlist) ||
  allowlist.some(
    (entry) => !entrySelectsAList(entry) && siteDiscoversLists(allowlist, entry, listsDefault)
  )

// Selector identity over SERVER entries (or a removal target spelled as one).
// The two families never cross: a list target matches only list entries, by
// name.
export const sameSelector = (entry, target) => {
  if (sitePathOf(entry) !== sitePathOf(target)) {
    return false
  }
  if (!isEmpty(normalizedLibrary(target.list_name))) {
    return entrySelectsAList(entry) && sameName(entry.list_name, target.list_name)
  }
  if (entrySelectsAList(entry)) {
    return false
  }
  return isSiteWideSelector(target.library_name)
    ? isSiteWideSelector(entry.library_name)
    : !isSiteWideSelector(entry.library_name) && sameLibraryName(entry, target)
}

// The form keeps the rows it was loaded with, so after a lists toggle each
// non-list row takes the include_lists the server now holds for its selector.
export const withServerIncludeLists = (rows, allowlist) =>
  rows.map((row) => {
    if (row.selector_type === LIST_TYPE) {
      return row
    }
    const target = toServerEntry(row)
    const match = allowlist.find((entry) => sameSelector(entry, target))
    return isNil(match) ? row : { ...row, include_lists: formIncludeLists(match) }
  })

// Would any of these allowlist entries still keep this sync row's content
// ingested? Mirrors sharepoint_removal.covered_by_remaining on the server:
//
// * a `pages` row needs a surviving NON-list entry for its site — Site Pages
//   sync per site regardless of the entry's library, but the resolver skips
//   list entries entirely (QCER-1089);
// * a `library` row needs a non-list entry that is site-wide or names it;
// * a `list` row needs a remaining entry naming it or, unless `siteDiscovery`
//   is off, a site that discovers lists.
export const stillSelectedBy = (
  entries,
  row,
  { siteDiscovery = true, listsDefault = false } = {}
) => {
  if (row.kind === 'list') {
    // Guarded by entrySelectsAList so two absent names can never compare equal,
    // the way the server's set of remaining list names is safe by construction.
    const named = entries.some(
      (entry) =>
        entrySelectsAList(entry) &&
        sameSite(entry, row) &&
        sameName(entry.list_name, row.library_name)
    )
    return named || (siteDiscovery && siteDiscoversLists(entries, row, listsDefault))
  }
  return entries.some((entry) => {
    if (sitePathOf(entry) !== sitePathOf(row)) {
      return false
    }
    if (row.kind === 'pages') {
      // Only a NON-list entry keeps a site's pages: the resolver skips list
      // entries when deciding which sites' Site Pages to sync (QCER-1089), so
      // a site left with nothing but list entries stops having pages ingested.
      return !entrySelectsAList(entry)
    }
    return (
      !entrySelectsAList(entry) &&
      (isSiteWideSelector(entry.library_name) || sameName(entry.library_name, row.library_name))
    )
  })
}

// The sync rows a removal would erase, for the confirmation body. The server is
// the authority; this only shapes the text, so it must not promise a deletion
// the server will refuse — rows the removal targets but an overlapping remaining
// entry keeps come back as `keptByOverlap` instead. `target` is server-shaped.
export const affectedSyncRows = (
  syncState,
  persistedAllowlist,
  target,
  { listsDefault = false } = {}
) => {
  if (isNil(syncState) || isEmpty(syncState.rows)) {
    return { rows: [], keptByOverlap: 0 }
  }
  const sitePath = sitePathOf(target)
  const removingAList = entrySelectsAList(target)
  const remaining = persistedAllowlist.filter((entry) => !sameSelector(entry, target))
  // A whole-site removal targets everything on the site; a library removal
  // targets its own library, plus the site's pages — which survive below unless
  // this was the site's last entry. A LIST removal (QCER-1089) targets only its
  // own list row, so removing one list never touches a sibling list, a library,
  // or the site's pages. Never-resolved selectors hold no documents.
  const targeted = syncState.rows.filter((row) => {
    if (row.kind === 'selector' || sitePathOf(row) !== sitePath) {
      return false
    }
    if (removingAList) {
      return row.kind === 'list' && sameName(row.library_name, target.list_name)
    }
    if (row.kind === 'list') {
      // A whole-site or last-library removal de-scopes the site, so its lists
      // go too unless a remaining entry still selects them (checked below).
      return true
    }
    return (
      isSiteWideSelector(target.library_name) ||
      row.kind === 'pages' ||
      sameName(row.library_name, target.library_name)
    )
  })
  // Removing a named list always takes its own row, as the server does; if the
  // site really discovers it, the next run re-adopts it.
  const rows = targeted.filter(
    (row) => !stillSelectedBy(remaining, row, { siteDiscovery: !removingAList, listsDefault })
  )
  return { rows, keptByOverlap: targeted.length - rows.length }
}

export const APP_NOT_APPROVED = 'app_not_approved'

// Operator-readable labels for the flow's low-cardinality error reasons; an
// unknown reason falls back to the raw code so new backend reasons still show.
// The chip's label sets the Status column's width, so it stays a couple of
// words; the sentence that explains what to do about it rides in the tooltip.
export const SYNC_ERROR_LABELS = {
  [APP_NOT_APPROVED]: {
    label: 'Needs approval',
    description:
      "Your Microsoft admin hasn't approved the Balto SharePoint app yet, so nothing can sync.",
  },
  access_denied: {
    label: 'Access denied',
    description: "Balto hasn't been granted read access to this site yet (Sites.Selected).",
  },
  graph_error: {
    label: 'Graph error',
    description: 'Microsoft Graph rejected the request — check the host and site path.',
  },
  library_not_found: {
    label: 'Library not found',
    description: 'The configured library no longer exists on this site.',
  },
  list_not_found: {
    label: 'List not found',
    description:
      'The configured list no longer exists on this site, was renamed, or names a document ' +
      'library or Site Pages — those are indexed as a Library row instead.',
  },
  no_libraries: {
    label: 'No libraries',
    description: 'The site exposes no document libraries to index.',
  },
  cap_exceeded: {
    label: 'Cap exceeded',
    description:
      'Stopped: this selection holds more files than Balto syncs. Choose a library, or contact Balto support.',
  },
  mass_deletion_guard: {
    label: 'Deletion guard',
    description:
      'Aborted — the run would have deleted most of this scope, which usually means a bad ' +
      'or empty enumeration rather than a real deletion.',
  },
  items_failed: {
    label: 'Items failed',
    description: 'Some items failed to ingest; the next run retries them.',
  },
  manifest_write_failed: {
    label: 'File list not saved',
    description:
      "Couldn't record this run's file list, so the run stopped there. The next run retries.",
  },
  manifest_unavailable: {
    label: 'File list unavailable',
    description:
      "Couldn't read the file list or the block list, so the run stopped there. The next run retries.",
  },
  // RTG3-1300: new files left out because the org is at its object ceiling.
  over_ceiling: {
    label: ORG_NEW_FILES_PAUSED_LABEL,
    description:
      'Some new files weren’t added: this organization’s knowledge base was at its size ' +
      'limit when this last synced. Changes and deletions still sync, and the files are ' +
      'added once it’s back under.',
  },
  unexpected: {
    label: 'Failed',
    description: 'Unexpected failure. Contact Balto support if this persists.',
  },
}

// The one multi-tenant Entra app every customer approves; the id is public and also in the
// customer setup guide.
export const BALTO_SHAREPOINT_APP_ID = 'dd2d5fea-e1cb-4cac-99f7-e37edf14c805'

// Docs Hub page; it redirects to login when signed out.
export const SHAREPOINT_SITE_ACCESS_GUIDE_URL =
  'https://docs.balto.ai/docs/connecting-your-sharepoint-to-balto#step-2--grant-read-access-to-each-site'

/**
 * The page where a tenant's Global Administrator approves the Balto SharePoint app.
 * @param {string} tenantId the customer's Entra tenant GUID
 * @returns {string} the admin consent URL to send them
 */
export const adminConsentUrl = (tenantId) =>
  `https://login.microsoftonline.com/${encodeURIComponent(tenantId)}/adminconsent?client_id=${BALTO_SHAREPOINT_APP_ID}`

/**
 * Whether the sync is blocked on the customer's Microsoft admin approving the app.
 * @param {Object} syncState undefined | null | the connection's ingest state
 * @returns {boolean} true when any row failed with app_not_approved
 */
export const awaitingAdminApproval = (syncState) =>
  some(syncState?.rows, (row) => row.last_error === APP_NOT_APPROVED)

export const syncRowLibraryLabel = (row) => {
  if (row.kind === 'pages') {
    return 'Site Pages'
  }
  // The list's name as the allowlist spells it (or its display name when the
  // row came from All lists), marked as a list so it is not mistaken for a
  // document library of the same name (QCER-1089).
  if (row.kind === 'list') {
    return `${row.library_name} (list)`
  }
  // A never-resolved selector with a blank or '*' library is a whole-site
  // selection (both spellings are site-wide to the resolver).
  if (row.kind === 'selector' && (isEmpty(row.library_name) || row.library_name === '*')) {
    return 'All libraries'
  }
  return row.library_name
}

// ---- Single-attribution owners for the merged content table (QCER-1073). ----
// Each sync row answers to exactly ONE entry so per-entry Documents sum to the
// connection's true total. The tie-breaks are stable: persistedEntries is
// connection.allowlist in server order, which a PATCH appends to and never
// reorders. A row whose owning entry is gone (its documents are being purged,
// or the state predates a config change) is attributed to no entry and drops
// from the table — the table is the config view, and the "State as of" line
// still dates the snapshot it summarizes.

const siteEntriesOf = (record, persistedEntries) =>
  persistedEntries.filter((candidate) => sitePathOf(candidate) === sitePathOf(record))

// Pages belong to the SITE and only non-list entries keep them syncing
// (QCER-1089): the site's whole-site entry when one exists, else the site's
// first non-list entry.
const isPagesOwner = (entry, persistedEntries) => {
  const candidates = siteEntriesOf(entry, persistedEntries).filter(
    (candidate) => !entrySelectsAList(candidate)
  )
  const owner =
    candidates.find((candidate) => isSiteWideSelector(candidate.library_name)) ?? candidates[0]
  return !isNil(owner) && sameSelector(owner, entry)
}

// A resolved library row when entries overlap (a whole-site entry plus an
// explicit same-site library entry): the entry naming the library exactly owns
// it; the whole-site entry owns the rest. List entries never own libraries.
const isLibraryRowOwner = (entry, persistedEntries, row) => {
  const candidates = siteEntriesOf(row, persistedEntries).filter(
    (candidate) => !entrySelectsAList(candidate)
  )
  const owner =
    candidates.find(
      (candidate) => !isSiteWideSelector(candidate.library_name) && sameLibraryName(candidate, row)
    ) ?? candidates.find((candidate) => isSiteWideSelector(candidate.library_name))
  return !isNil(owner) && sameSelector(owner, entry)
}

// A resolved list row: the entry naming it, else, on a site that discovers
// lists, the site's whole-site entry or its first non-list entry.
const isListRowOwner = (entry, persistedEntries, row, listsDefault) => {
  const candidates = persistedEntries.filter((candidate) => sameSite(candidate, row))
  const owner =
    candidates.find(
      (candidate) => entrySelectsAList(candidate) && sameName(candidate.list_name, row.library_name)
    ) ??
    (siteDiscoversLists(persistedEntries, row, listsDefault)
      ? candidates.find(
          (candidate) => !entrySelectsAList(candidate) && isSiteWideSelector(candidate.library_name)
        ) ?? candidates.find((candidate) => !entrySelectsAList(candidate))
      : undefined)
  return !isNil(owner) && sameSelector(owner, entry)
}

// A never-resolved selector row carries the selector's own spelling in
// {site_path, library_name} — for a list selector the flow writes the LIST
// name there. An entry naming it exactly owns it (library family first, list
// family second); a site-wide selector row belongs to the site's whole-site
// entry.
const isSelectorRowOwner = (entry, persistedEntries, row) => {
  const candidates = siteEntriesOf(row, persistedEntries)
  const owner =
    candidates.find(
      (candidate) =>
        !entrySelectsAList(candidate) &&
        !isSiteWideSelector(candidate.library_name) &&
        sameName(candidate.library_name, row.library_name)
    ) ??
    candidates.find(
      (candidate) => entrySelectsAList(candidate) && sameName(candidate.list_name, row.library_name)
    ) ??
    (isSiteWideSelector(row.library_name)
      ? candidates.find(
          (candidate) => !entrySelectsAList(candidate) && isSiteWideSelector(candidate.library_name)
        )
      : undefined)
  return !isNil(owner) && sameSelector(owner, entry)
}

/**
 * The sync rows one persisted allowlist entry answers for, partitioning the
 * connection's sync state across its entries with no double counting. `entry`
 * and `persistedEntries` are SERVER-shaped (the form maps its rows through
 * toServerEntry before asking).
 */
export const sharePointSyncRowsFor = (
  entry,
  persistedEntries,
  syncRows,
  { listsDefault = false } = {}
) =>
  syncRows.filter((row) => {
    // List rows follow the case-insensitive list rules; the rest mirror the
    // server's exact-path removal targeting.
    const siteMatches =
      row.kind === 'list' ? sameSite(row, entry) : sitePathOf(row) === sitePathOf(entry)
    if (!siteMatches) {
      return false
    }
    if (row.kind === 'selector') {
      return isSelectorRowOwner(entry, persistedEntries, row)
    }
    if (row.kind === 'pages') {
      return isPagesOwner(entry, persistedEntries)
    }
    if (row.kind === 'list') {
      return isListRowOwner(entry, persistedEntries, row, listsDefault)
    }
    return isLibraryRowOwner(entry, persistedEntries, row)
  })

/**
 * The Manage files roots for a row's covered sync rows: one per document library. Site Pages
 * and lists are outside Manage files, and a never-resolved selector has no drive to browse.
 * @returns {ManageFilesRoot[]} typedef in ManageFilesModal.helpers.js
 */
export const sharePointManageFilesRoots = (covered) =>
  covered
    .filter((row) => row.kind === 'library' && !isEmpty(row.drive_id))
    .map((row) => ({
      driveId: row.drive_id,
      rootId: null,
      label: row.library_name,
      lastSyncedAt: row.last_synced_at,
    }))
